import type { AnalyticDateFilter } from '~/presentation/ui';
import type { IPAChartLabels } from '../types';

import { dayjsTz, dayjsTzToLocalTZ } from '~/utils/dayjs';
import { ETimeDimension } from '~/core/gemxql/types';

const CHART_LABELS_MAP = {
  [ETimeDimension.HOUR]: {
    format: 'h A',
    tooltipFormat: 'MMM DD, YYYY, h:mm A',
  },
  [ETimeDimension.DAY]: {
    format: 'MMM D',
    tooltipFormat: 'MMM DD, YYYY',
  },
  [ETimeDimension.MONTH]: {
    format: 'YYYY-MM',
    tooltipFormat: 'MMM YYYY',
  },
};

export const getChartLabels = (
  interval: ETimeDimension,
  dateFilter: AnalyticDateFilter,
): IPAChartLabels => {
  const startDate = dayjsTzToLocalTZ(dateFilter.startDate);
  const endDate = dayjsTzToLocalTZ(dateFilter.endDate);

  switch (interval) {
    case ETimeDimension.HOUR: {
      const hours: IPAChartLabels = [];
      let currentDate = startDate.startOf('day');
      const endDateDay = endDate.endOf('day');

      const { format, tooltipFormat } = CHART_LABELS_MAP[ETimeDimension.HOUR];

      while (currentDate.isBefore(endDateDay) || currentDate.isSame(endDateDay, 'day')) {
        for (let hour = 0; hour < 24; hour++) {
          const date = currentDate.hour(hour).minute(0).second(0).millisecond(0);
          const value = date.format(format);
          const tooltipValue = date.format(tooltipFormat);

          hours.push({
            value,
            tooltipValue,
          });
        }
        currentDate = currentDate.add(1, 'day');
      }
      return hours;
    }
    case ETimeDimension.DAY: {
      // return days in the range
      const days: IPAChartLabels = [];
      let currentDate = startDate.startOf('day');
      const endDateDay = endDate.endOf('day');

      const { format, tooltipFormat } = CHART_LABELS_MAP[ETimeDimension.DAY];

      while (currentDate.isBefore(endDateDay) || currentDate.isSame(endDateDay, 'day')) {
        const value = currentDate.format(format);
        const tooltipValue = currentDate.format(tooltipFormat);

        days.push({
          value,
          tooltipValue,
        });
        currentDate = currentDate.add(1, 'day');
      }
      return days;
    }
    case ETimeDimension.MONTH: {
      // return months in the range
      const months: IPAChartLabels = [];
      let currentDate = startDate.startOf('month');
      const endDateMonth = endDate.endOf('month');

      const { format, tooltipFormat } = CHART_LABELS_MAP[ETimeDimension.MONTH];

      while (currentDate.isBefore(endDateMonth) || currentDate.isSame(endDateMonth, 'month')) {
        const value = currentDate.format(format);
        const tooltipValue = currentDate.format(tooltipFormat);

        months.push({
          value,
          tooltipValue,
        });
        currentDate = currentDate.add(1, 'month');
      }
      return months;
    }
    default:
      return [];
  }
};

export const getPeriodChartName = (dateFilter: AnalyticDateFilter): string => {
  const { startDate, endDate } = dateFilter;
  if (!startDate || !endDate) return 'Unknown Period';

  const startDateObj = startDate;
  const endDateObj = endDate;

  const startYear = startDateObj.year();
  const endYear = endDateObj.year();
  const startMonth = startDateObj.month();
  const endMonth = endDateObj.month();
  const startDay = startDateObj.date();
  const endDay = endDateObj.date();

  // Case: Same day month year
  if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
    return startDateObj.format('MMM D, YYYY');
  }

  // Case: Same year and same month
  if (startYear === endYear && startMonth === endMonth) {
    const monthName = startDateObj.format('MMM');
    return `${monthName} ${startDay}–${endDay}, ${startYear}`;
  }

  // Case: Same year but different month
  if (startYear === endYear) {
    const startMonthName = startDateObj.format('MMM');
    const endMonthName = endDateObj.format('MMM');
    return `${startMonthName} ${startDay}–${endMonthName} ${endDay}, ${startYear}`;
  }

  // Case: Different year
  const startMonthName = startDateObj.format('MMM');
  const endMonthName = endDateObj.format('MMM');
  return `${startMonthName} ${startDay}, ${startYear}–${endMonthName} ${endDay}, ${endYear}`;
};

export const formatDateLabel = (date: string, interval: ETimeDimension) => {
  const labels = CHART_LABELS_MAP[interval as keyof typeof CHART_LABELS_MAP];
  if (!labels) return { key: date, tooltipKey: date };

  const d = dayjsTz(date);
  return { key: d.format(labels.format), tooltipKey: d.format(labels.tooltipFormat) };
};
